import type Model from '@ember-data/model';
import type Store from '@ember-data/store';
import type ModelRegistry from '@ember-data/types/registries/model';
import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import type IntlService from 'ember-intl/services/intl';
import eq from 'ember-truth-helpers/helpers/eq';
import LinkToForc from 'parog-web/components/link-to-forc';
import { getPrescriptionJournalEventComponent } from 'parog-web/components/prescription/utils/prescription-helpers';
import capitalize from 'parog-web/helpers/capitalize';
import contextualRoute from 'parog-web/helpers/contextual-route';
import BioExamPrescribedJournalEvent from 'parog-web/models/bio-exam-prescribed-journal-event';
import FileUploadedJournalEvent from 'parog-web/models/file-uploaded-journal-event';
import type JournalEvent from 'parog-web/models/journal-event';
import MedicalImagingPrescribedJournalEvent from 'parog-web/models/medical-imaging-prescribed-journal-event';
import MedicationPrescribedJournalEvent from 'parog-web/models/medication-prescribed-journal-event';
import type NotificationTriggeredJournalEvent from 'parog-web/models/notification-triggered-journal-event';
import ParamedicalCarePrescribedJournalEvent from 'parog-web/models/paramedical-care-prescribed-journal-event';
import type PatientChatMessageSentJournalEvent from 'parog-web/models/patient-chat-message-sent-journal-event';
import type PrescriptionJournalEvent from 'parog-web/models/prescription-journal-event';
import type SemeiaSessionService from 'parog-web/services/session';
import LinkButton from 'ui-kit/components/link-button';
import TimeZoneWithWarning from 'ui-kit/components/timezone-with-warning';

export const ChatMessageInterlocutor = {
  Sender: 'sender',
  Recipient: 'recipient',
  Recipient_Alternate: 'recipient-alternate'
};

interface ChatMessageFeedMessageComponentArgs {
  messageEvent: PatientChatMessageSentJournalEvent;
  isLastMessage: boolean;
}

export default class ChatMessageFeedMessageComponent extends Component<ChatMessageFeedMessageComponentArgs> {
  @service declare intl: IntlService;
  @service declare session: SemeiaSessionService;
  @service declare store: Store;

  get callbackUrl(): string {
    const patientId = this.args.messageEvent.patient.get('id');
    return this.session.medic
      ? `/medics-web/patients/${patientId}/chat`
      : '/patient-web/chat';
  }

  get interlocutorDisplayName(): string {
    if (this.interlocutorType === ChatMessageInterlocutor.Sender) {
      return this.intl.t('chat.interlocutors.sender');
    }

    if (this.#isMessageSentByMedic) {
      const creator = this.#creator as ModelRegistry['medic'];
      const prefix =
        creator.role === 'doctor'
          ? this.intl.t('chat.interlocutors.doctor') + ' '
          : '';
      return prefix + creator.fullName;
    } else if (this.#isMessageSentByPatient) {
      return this.intl.t('chat.interlocutors.patient');
    }
    return this.intl.t('chat.interlocutors.program');
  }

  get interlocutorType(): string {
    if (!this.#creator) {
      return ChatMessageInterlocutor.Recipient;
    }

    if (
      this.#isMessageSentByMedic &&
      this.session.medic?.id == this.#creator.id
    ) {
      return ChatMessageInterlocutor.Sender;
    } else if (
      this.#isMessageSentByPatient &&
      this.session.patient?.id == this.#creator.id
    ) {
      return ChatMessageInterlocutor.Sender;
    } else if (this.#isMessageSentByMedic && this.session.medic) {
      return ChatMessageInterlocutor.Recipient_Alternate;
    }
    return ChatMessageInterlocutor.Recipient;
  }

  get isReadOnly(): boolean {
    return this.session.patient != null;
  }

  get isTriggeredByPrescription(): boolean {
    return (
      this.triggeringJournalEvent instanceof BioExamPrescribedJournalEvent ||
      this.triggeringJournalEvent instanceof MedicationPrescribedJournalEvent ||
      this.triggeringJournalEvent instanceof
        MedicalImagingPrescribedJournalEvent ||
      this.triggeringJournalEvent instanceof
        ParamedicalCarePrescribedJournalEvent
    );
  }

  get message() {
    return this.args.messageEvent.body;
  }

  get prescriptionRoute(): string | undefined {
    if (this.session.medic) {
      return `medics-web.protected.patients.patient.prescriptions.${this.#prescriptionComponent}`;
    }

    return `patient-web.protected.prescriptions.${this.#prescriptionComponent}`;
  }

  get readAtSignature(): string {
    return `Lu, le ${dayjs(this.args.messageEvent.readByPatientAt).format(
      'DD/MM/YYYY'
    )}`;
  }

  get shouldDisplayReadAtSignature(): boolean {
    return (
      !!this.session.medic &&
      this.args.messageEvent.readByPatientAt &&
      this.#isMessageSentByMedic
    );
  }

  get shouldShowUnreadMessage(): boolean {
    return (
      this.args.isLastMessage &&
      !!this.session.medic &&
      !this.session.medic.disabledNotificationTypes?.includes(
        'PatientChatMessageSentJournalEvent'
      ) &&
      this.#isMessageSentByPatient &&
      (this.args.messageEvent.notificationTriggeredJournalEvents.length == 0 ||
        (
          this.args.messageEvent
            .notificationTriggeredJournalEvents as NotificationTriggeredJournalEvent[]
        ).every(je => je.hasBeenProcessed))
    );
  }

  get signature(): string {
    return `${this.interlocutorDisplayName} le ${dayjs(
      this.args.messageEvent.occurredAt
    ).format('DD/MM/YYYY à HH:mm')}`;
  }

  get triggeringJournalEvent(): JournalEvent {
    return this.args.messageEvent.triggeringJournalEvent;
  }

  get triggeringJournalEventMessage(): string {
    const date = dayjs(this.triggeringJournalEvent.occurredAt).format(
      'DD/MM/YYYY'
    );

    if (this.triggeringJournalEvent instanceof FileUploadedJournalEvent) {
      return `${date} - ${
        (this.triggeringJournalEvent as FileUploadedJournalEvent).title
      }`;
    }

    return date;
  }

  get #creator() {
    return this.args.messageEvent.creator;
  }

  get #creatorType() {
    return (this.#creator?.constructor as typeof Model)?.modelName;
  }

  get #isMessageSentByMedic() {
    return this.#creatorType === 'medic';
  }

  get #isMessageSentByPatient() {
    return this.#creatorType === 'patient';
  }

  get #prescriptionComponent(): string | undefined {
    return getPrescriptionJournalEventComponent(
      this.triggeringJournalEvent as PrescriptionJournalEvent
    );
  }

  unreadMessage = task(async () => {
    await this.store
      .createRecord('notification-triggered-journal-event', {
        occurredAt: this.args.messageEvent.occurredAt,
        patient: this.args.messageEvent.patient,
        triggeringJournalEvent: this.args.messageEvent
      })
      .save();
  });

  <template>
    <div class='{{this.interlocutorType}} message' data-test-message>
      <div class='content'>
        {{#if this.triggeringJournalEvent}}
          {{#if this.isTriggeredByPrescription}}
            <span class='pre-line'>{{htmlSafe this.message}}</span>
            <br />

            <LinkTo
              @route={{this.prescriptionRoute}}
              @model={{this.triggeringJournalEvent}}
              data-test-prescription-link
            >
              <i class='file icon'></i>{{this.triggeringJournalEventMessage}}
            </LinkTo>
          {{else if
            (eq
              this.triggeringJournalEvent.summary.type
              'bio-exam-results-received-journal-event'
            )
          }}
            <span class='pre-line'>{{htmlSafe this.message}}</span>
            <br />
            {{#if this.triggeringJournalEvent.summary.forcIdentifier.feature}}
              <LinkTo
                @route='{{contextualRoute
                  this.triggeringJournalEvent.summary.forcIdentifier.feature
                }}.{{this.triggeringJournalEvent.summary.forcIdentifier.component}}'
                @query={{hash sourceJeId=this.triggeringJournalEvent.id}}
                data-test-link-to-form={{this.triggeringJournalEvent.id}}
              >
                <i class='file icon'></i>{{this.triggeringJournalEventMessage}}
              </LinkTo>
            {{/if}}
          {{else}}
            <span class='pre-line'>{{htmlSafe this.message}}</span>
            <br />
            <LinkToForc
              @patient={{this.triggeringJournalEvent.patient}}
              {{! @glint-expect-error this.triggeringJournalEvent.summary.forcIdentifier.feature can be undefined }}
              @feature={{this.triggeringJournalEvent.summary.forcIdentifier.feature}}
              {{! @glint-expect-error this.triggeringJournalEvent.summary.forcIdentifier.component can be undefined }}
              @component={{this.triggeringJournalEvent.summary.forcIdentifier.component}}
              @sourceJeId={{this.triggeringJournalEvent.id}}
              @callbackUrl={{this.callbackUrl}}
              @isReadOnly={{this.isReadOnly}}
              data-test-link-to-form={{this.triggeringJournalEvent.id}}
            >
              <i class='file icon'></i>{{this.triggeringJournalEventMessage}}
            </LinkToForc>
          {{/if}}
        {{else}}
          <span class='pre-line'>{{htmlSafe this.message}}</span>
        {{/if}}
      </div>
      <div
        class='signature time-label mt-[5px]'
        data-test-message-by={{this.interlocutorDisplayName}}
      >
        <TimeZoneWithWarning
          @date={{@messageEvent.occurredAt}}
          @originalOffsetInSeconds={{@messageEvent.occurredAtOffsetInSeconds}}
          @tooltipPosition='left center'
        >
          {{capitalize this.signature}}
        </TimeZoneWithWarning>
      </div>
      {{#if this.shouldDisplayReadAtSignature}}
        <time
          datetime={{this.readAtSignature}}
          class='signature time-label'
          data-test-read-at={{this.interlocutorDisplayName}}
        >{{this.readAtSignature}}</time>

      {{/if}}
      {{#if this.shouldShowUnreadMessage}}
        <div class='unread signature primary' data-test-unread>
          <LinkButton
            {{on 'click' (perform this.unreadMessage)}}
            data-test-unread-button
          >
            Marquer le dernier message comme non lu
          </LinkButton>
        </div>
      {{/if}}
    </div>
  </template>
}
